import { Bot, User } from "lucide-react";
import React from "react";

import { cn } from "@/lib/utils";

import {
  formatMessageTime,
  sourceLabel,
} from "../partner-whatsapp-inbox.utils";

function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/^[\s]*[-*]\s+/gm, "• ")
    .trim();
}

function linkifyText(text: string): React.ReactNode {
  const cleaned = stripMarkdown(text);
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = cleaned.split(urlRegex);
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="underline break-all opacity-90 hover:opacity-100"
      >
        {part}
      </a>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  );
}

export interface MessageBubbleProps {
  direction: "inbound" | "outbound";
  body: string;
  sentAt: string;
  source?: string;
  locale?: string;
  translate: (key: string) => string;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  direction,
  body,
  sentAt,
  source,
  locale = "pt-BR",
  translate,
}) => {
  const isOutbound = direction === "outbound";
  const timeLabel = formatMessageTime(sentAt, locale);
  const src = source ? sourceLabel(source, translate) : "";

  return (
    <div
      className={cn(
        "flex gap-2 max-w-[min(85%,28rem)]",
        isOutbound ? "ml-auto flex-row-reverse" : "mr-auto",
      )}
    >
      <div
        className={cn(
          "h-7 w-7 rounded-full shrink-0 flex items-center justify-center mt-0.5",
          isOutbound
            ? "bg-emerald-600/20 text-emerald-600 dark:text-emerald-400"
            : "bg-muted text-muted-foreground",
        )}
      >
        {isOutbound ? (
          <Bot className="h-3.5 w-3.5" />
        ) : (
          <User className="h-3.5 w-3.5" />
        )}
      </div>
      <div
        className={cn(
          "rounded-2xl px-3.5 py-2.5 shadow-sm",
          isOutbound
            ? "rounded-tr-md bg-emerald-600 text-white dark:bg-emerald-700"
            : "rounded-tl-md bg-card border border-border/80",
        )}
      >
        <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
          {linkifyText(body)}
        </p>
        <p
          className={cn(
            "text-[10px] mt-1.5 flex items-center gap-1.5 tabular-nums",
            isOutbound ? "text-white/75" : "text-muted-foreground",
          )}
        >
          <span>{timeLabel}</span>
          {src ? (
            <>
              <span aria-hidden>·</span>
              <span>{src}</span>
            </>
          ) : null}
        </p>
      </div>
    </div>
  );
};
